/**
 * Sliders body -> canonical expression text (shared between FE/BE).
 * For Media Devoted | Created By Ciro Autiero
 *
 * Sliders mode is always a weighted sum of the picked metrics:
 *   metric_a * 0.40 + metric_b * 0.35 + metric_c * 0.25
 * A row picked "Lowest" (02/11/2026) reads `lowest(metric_c) * 0.25`.
 *
 * Used by:
 *  - Frontend: pre-fills FormulaGroup.expression on every edit.
 *  - Backend: re-derives the effective expression at evaluation time so a
 *    config persisted with a stale `expression: "0"` (saved before the user
 *    picked any metric) doesn't poison the optimizer once the sliders body
 *    has real metric rows.
 */

import type {
  FormulaSlidersBody,
  FormulaSubMetric,
} from "../voluum-flow-optimizer.types";

export interface SliderCoefficient {
  metric: string;
  /** The exact multiplier the expression uses (2 decimals, like the text). */
  coefficient: number;
  /** Ranked by the lowest value — `lowest(metric)` in the expression. */
  lowest?: boolean;
}

/**
 * The multipliers the sliders really run with — weights normalized to their
 * total (equal split when all are 0), rounded to 2 decimals exactly as the
 * expression text writes them. Every reader of slider math (the expression,
 * the contributions a hover shows) uses THIS, so the shown parts always add
 * up to the score.
 */
export function sliderCoefficients(
  body: FormulaSlidersBody | null | undefined,
): SliderCoefficient[] {
  const usable = (body?.metrics ?? []).filter((m) => Boolean(m.metric));
  if (usable.length === 0) return [];
  const total = usable.reduce((sum, m) => sum + Math.max(0, m.weight), 0);
  if (total <= 0) {
    const coefficient = Number((1 / usable.length).toFixed(2));
    return usable.map((m) => part(m, coefficient));
  }
  return usable
    .filter((m) => m.weight > 0)
    .map((m) => part(m, Number((m.weight / total).toFixed(2))));
}

function part(m: FormulaSubMetric, coefficient: number): SliderCoefficient {
  return { metric: m.metric, coefficient, ...(m.lowest ? { lowest: true } : {}) };
}

export function serializeSliders(
  body: FormulaSlidersBody | null | undefined,
): string {
  const parts = sliderCoefficients(body);
  if (parts.length === 0) return "0";
  return parts
    .map((p) => `${p.lowest ? `lowest(${p.metric})` : p.metric} * ${p.coefficient.toFixed(2)}`)
    .join(" + ");
}
